import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { CalendarDays, Clock, User } from 'lucide-react';
import { useVacationRequests } from '../../hooks/useVacationRequests';
import { useHolidays } from '../../hooks/useHolidays';
import { toast } from 'sonner';
import { chileanInputDateToISO } from '../../utils/dateUtils';

export const AdministrativeRequestDialog = ({ employee, open, onOpenChange, onRequestCreated }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState('full'); // 'full' o 'half'
  const [notes, setNotes] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculatedDays, setCalculatedDays] = useState(0);

  const { createRequest } = useVacationRequests();
  const { calculateWorkingDays } = useHolidays();

  const availableAdminDays = (employee?.admin_days_available ?? employee?.adminDaysAvailable) || 0;

  // Calcular días cuando cambien las fechas o el tipo
  useEffect(() => {
    const calcDays = async () => {
      if (!startDate || !endDate) {
        setCalculatedDays(0);
        return;
      }

      setIsCalculating(true);
      try {
        const response = await calculateWorkingDays(startDate, endDate);
        if (response.success) {
          const days = type === 'half' ? response.data.workingDays * 0.5 : response.data.workingDays;
          setCalculatedDays(days);
        }
      } catch (error) {
        console.error('Error calculating days:', error);
        setCalculatedDays(0);
      } finally {
        setIsCalculating(false);
      }
    };

    if (startDate && endDate) {
      calcDays();
    }
  }, [startDate, endDate, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate || !endDate) {
      toast.error('Por favor seleccione las fechas');
      return;
    }

    if (new Date(chileanInputDateToISO(startDate)) > new Date(chileanInputDateToISO(endDate))) {
      toast.error('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }

    if (calculatedDays <= 0) {
      toast.error('El rango de fechas debe incluir al menos un día hábil');
      return;
    }

    if (calculatedDays > availableAdminDays) {
      toast.error(`No tiene suficientes días administrativos disponibles (${availableAdminDays} días)`);
      return;
    }

    try {
      const requestData = {
        employeeId: employee.id || employee._id,
        // Enviar YYYY-MM-DD directamente; el backend lo acepta y evita ambigüedad TZ
        startDate: startDate,
        endDate: endDate,
        type: 'administrative', // Tipo específico para días administrativos
        requestType: type, // 'full' o 'half'
        totalDays: calculatedDays,
        notes: notes || `Solicitud de ${calculatedDays} día${calculatedDays !== 1 ? 's' : ''} administrativo${calculatedDays !== 1 ? 's' : ''}`
      };

      console.log('Enviando solicitud administrativa:', requestData);
      
      const response = await createRequest(requestData);
      if (response.success) {
        toast.success('Solicitud administrativa enviada exitosamente');
        onRequestCreated?.(response.data);
        onOpenChange(false);
        
        // Limpiar formulario
        setStartDate('');
        setEndDate('');
        setType('full');
        setNotes('');
        setCalculatedDays(0);
      } else {
        throw new Error(response.message || 'Error al crear la solicitud');
      }
    } catch (error) {
      console.error('Error creating administrative request:', error);
      toast.error(error.message || 'Error al enviar la solicitud administrativa');
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setStartDate('');
    setEndDate('');
    setType('full');
    setNotes('');
    setCalculatedDays(0);
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays size={20} className="text-blue-500" />
            Solicitar Días Administrativos
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info del Empleado */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <User size={16} className="text-blue-600" />
              <span className="font-medium">{employee.name}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Días Administrativos Disponibles:</span>
              <Badge variant={availableAdminDays > 0 ? 'default' : 'destructive'}>
                {availableAdminDays} días
              </Badge>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tipo de Solicitud */}
            <div className="space-y-2">
              <Label>Tipo de Solicitud</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={type === 'full' ? 'default' : 'outline'}
                  onClick={() => setType('full')}
                  className="w-full"
                >
                  Día Completo
                </Button>
                <Button
                  type="button"
                  variant={type === 'half' ? 'default' : 'outline'}
                  onClick={() => setType('half')}
                  className="w-full"
                >
                  Medio Día
                </Button>
              </div>
            </div>

            {/* Fechas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Fecha Inicio</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">Fecha Fin</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                />
              </div>
            </div>

            {/* Días Calculados */}
            {startDate && endDate && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-gray-500" />
                    <span className="text-sm font-medium">Días Administrativos a Solicitar:</span>
                  </div>
                  <div className="text-right">
                    {isCalculating ? (
                      <span className="text-sm text-gray-500">Calculando...</span>
                    ) : (
                      <Badge variant={calculatedDays > availableAdminDays ? 'destructive' : 'default'}>
                        {calculatedDays} día{calculatedDays !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
                {calculatedDays > availableAdminDays && (
                  <p className="text-xs text-red-600 mt-1">
                    Excede los días administrativos disponibles
                  </p>
                )}
              </div>
            )}

            {/* Notas */}
            <div className="space-y-2">
              <Label htmlFor="notes">Motivo/Observaciones (Opcional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Motivo de la solicitud de días administrativos..."
                rows={3}
              />
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={!startDate || !endDate || calculatedDays <= 0 || calculatedDays > availableAdminDays || isCalculating}
              >
                Enviar Solicitud
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
